import React, { useState, useEffect, useCallback, useRef } from 'react'
import rawDataService, { ProjectNode, FileItem, ProjectStructure, UploadBatchItem, TriggerLabelSchema, FileSearchResult } from '../services/rawDataService'
import { hasPermission } from '../utils/permissions'
import { PERMISSIONS } from '../constants/permissions'
import CreateRawDataModal, { RawDataFormData } from '../components/CreateRawDataModal'
import ManualUploadModal from '../components/ManualUploadModal'
import UploadHistorySection from '../components/UploadHistorySection'
import config from '../config'
import { formatShortDate } from '../utils/date'
import systemService, { TriggerLabelSchemas } from '../services/systemService'

interface RawDataManagementProps {
  userInfo: {
    email: string
    fullName: string
    isAdmin: boolean
    permissions: string[]
  }
  initialView?: 'manual' | 'auto'
  hideViewToggle?: boolean
}

export default function RawDataManagement({ userInfo, initialView = 'manual', hideViewToggle = false }: RawDataManagementProps) {
  const [selectedNode, setSelectedNode] = useState<ProjectNode | null>(null)
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const [projectStructure, setProjectStructure] = useState<ProjectStructure | null>(null)
  const [nodeFiles, setNodeFiles] = useState<FileItem[]>([])
  const [filesSkip, setFilesSkip] = useState(0)
  const [filesHasMore, setFilesHasMore] = useState(true)
  const [filesTotal, setFilesTotal] = useState<number | null>(null)
  const [fileCounts, setFileCounts] = useState<Record<number, number>>({})
  const [childCounts, setChildCounts] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(() => false)
  const [pendingUploads, setPendingUploads] = useState<{
    key: string
    session_id: string
    filename: string
    received?: number
    total_size?: number
  }[]>([])
  const [uploadView, setUploadView] = useState<'manual' | 'auto'>(initialView)
  const resumeFileInputRef = useRef<HTMLInputElement | null>(null)
  const [resumeTarget, setResumeTarget] = useState<{ key: string; session_id: string; filename: string } | null>(null)
  const [selectedBatch, setSelectedBatch] = useState<UploadBatchItem | null>(null)
  // 파일 목록 요청 취소 컨트롤러: 노드/배치 연타 시 이전 요청을 취소해
  // 서버 부하를 줄이고, 오래된 응답이 새 선택을 덮어쓰는 레이스를 방지한다.
  const fileListAbortRef = useRef<AbortController | null>(null)
  const beginFileListRequest = useCallback((): AbortController => {
    if (fileListAbortRef.current) {
      try { fileListAbortRef.current.abort() } catch {}
    }
    const ctrl = new AbortController()
    fileListAbortRef.current = ctrl
    return ctrl
  }, [])
  const isAbortError = (e: any): boolean => {
    if (!e) return false
    if (e.name === 'AbortError') return true
    if (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'AbortError') return true
    return false
  }
  useEffect(() => () => {
    if (fileListAbortRef.current) {
      try { fileListAbortRef.current.abort() } catch {}
      fileListAbortRef.current = null
    }
    if (downloadAbortRef.current) {
      downloadAbortRef.current.cancelled = true
    }
  }, [])
  const [descExpanded, setDescExpanded] = useState<boolean>(false)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [triggerLabels, setTriggerLabels] = useState<TriggerLabelSchemas | null>(null)
  const [manualPreprocessModes, setManualPreprocessModes] = useState<{ face: boolean; plate: boolean }>({
    face: false,
    plate: false,
  })

  // 검색 상태
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDateFrom, setSearchDateFrom] = useState('')
  const [searchDateTo, setSearchDateTo] = useState('')
  const [searchScopeNode, setSearchScopeNode] = useState(true)
  const [searchResults, setSearchResults] = useState<FileSearchResult[] | null>(null)
  const [searchTotal, setSearchTotal] = useState<number | null>(null)
  const [searchSkip, setSearchSkip] = useState(0)
  const [searchHasMore, setSearchHasMore] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadStatusMsg, setDownloadStatusMsg] = useState('')
  const downloadAbortRef = useRef<{ cancelled: boolean } | null>(null)

  const canManualTab = hasPermission(userInfo, PERMISSIONS.RAW_DATA_MANUAL_TAB)
  const canAutoTab = hasPermission(userInfo, PERMISSIONS.RAW_DATA_AUTO_TAB)
  // 프로젝트 루트 자체를 새로 만들 수 있는지 여부 (관리자 또는 project_create 전역 권한)
  const canDirectRawProjectInput = hasPermission(userInfo, 'project_create')

  // 현재 사용자에게 '구조 생성(그룹/스트림)' 권한이 있는 수동 업로드 프로젝트가 하나라도 있는지
  const hasAnyCreateProjectPermission = (() => {
    if (!projectStructure?.nodes) return false
    // depth 0 = 루트 노드(프로젝트)
    return projectStructure.nodes.some((n: ProjectNode) => {
      const isRoot = n.parent_id == null
      const actions = (n as any).actions as Record<string, boolean> | undefined
      return isRoot && !!actions?.create
    })
  })()

  const sortNodesByName = useCallback(
    (nodes: ProjectNode[]) =>
      [...nodes].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })),
    []
  )

  const buildSearchParams = useCallback(() => ({
    q: searchQuery.trim() || undefined,
    dateFrom: searchDateFrom || undefined,
    dateTo: searchDateTo || undefined,
    nodeId: searchScopeNode && selectedNode ? selectedNode.id : undefined,
    uploadMethod: uploadView as 'manual' | 'auto',
  }), [searchQuery, searchDateFrom, searchDateTo, searchScopeNode, selectedNode, uploadView])

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() && !searchDateFrom && !searchDateTo) { clearSearch(); return }
    setSearchLoading(true)
    setSearchResults(null)
    setSearchSkip(0)
    setSearchTotal(null)
    try {
      const params = buildSearchParams()
      const [results, total] = await Promise.all([
        rawDataService.searchFiles({ ...params, skip: 0, limit: 50 }),
        rawDataService.searchFilesCount(params),
      ])
      setSearchResults(results)
      setSearchSkip(results.length)
      setSearchHasMore(results.length === 50)
      setSearchTotal(total)
    } catch {
      setSearchResults([])
      setSearchTotal(0)
    } finally {
      setSearchLoading(false)
    }
  }, [buildSearchParams])

  const handleSearchLoadMore = useCallback(async () => {
    if (searchLoading || !searchResults) return
    setSearchLoading(true)
    try {
      const more = await rawDataService.searchFiles({ ...buildSearchParams(), skip: searchSkip, limit: 50 })
      setSearchResults(prev => [...(prev || []), ...more])
      setSearchSkip(prev => prev + more.length)
      setSearchHasMore(more.length === 50)
    } catch {}
    finally { setSearchLoading(false) }
  }, [searchLoading, searchResults, buildSearchParams, searchSkip])

  // 대용량 ZIP은 blob 변환 없이 브라우저 기본 다운로드로 위임하여 메모리 사용을 피한다 (배치 다운로드와 동일 방식)
  const triggerNativeDownload = (url: string) => {
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    a.click()
  }

  const handleSearchDownload = useCallback(async () => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.cancelled = true
    }
    const abort = { cancelled: false }
    downloadAbortRef.current = abort

    setDownloadLoading(true)
    setDownloadStatusMsg('요청 중...')
    try {
      const params = buildSearchParams()
      const result = await rawDataService.requestSearchDownload(params)
      if (abort.cancelled) return

      if (result.status === 'stream') {
        triggerNativeDownload(result.url)
        return
      }

      // 202: 백그라운드 패키징 중 → 폴링
      if (result.status === 'processing' && result.pollingUrl) {
        setDownloadStatusMsg('패키징 중...')
        const pollingUrl = result.pollingUrl
        while (!abort.cancelled) {
          await new Promise(r => setTimeout(r, 3000))
          if (abort.cancelled) return
          try {
            const statusRes = await rawDataService.checkDownloadStatus(pollingUrl)
            if (abort.cancelled) return
            if (statusRes.status === 'ready') {
              setDownloadStatusMsg('다운로드 중...')
              triggerNativeDownload(result.url)
              return
            }
            if (statusRes.status === 'failed') {
              if (!abort.cancelled) alert('패키징에 실패했습니다.')
              return
            }
            // 진행률 포함 서버 메시지 표시 (예: "패키징이 진행 중입니다. (43%)")
            setDownloadStatusMsg(statusRes.message || '패키징 중...')
          } catch {}
        }
      }
    } catch {
      if (!abort.cancelled) alert('다운로드 요청에 실패했습니다.')
    } finally {
      if (!abort.cancelled) {
        setDownloadLoading(false)
        setDownloadStatusMsg('')
        downloadAbortRef.current = null
      }
    }
  }, [buildSearchParams])

  const clearSearch = useCallback(() => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.cancelled = true
      downloadAbortRef.current = null
    }
    setDownloadLoading(false)
    setDownloadStatusMsg('')
    setSearchResults(null)
    setSearchTotal(null)
    setSearchQuery('')
    setSearchDateFrom('')
    setSearchDateTo('')
  }, [])

  useEffect(() => {
    // 초기 업로드 뷰는 권한과 initialView 를 고려해 결정
    if (initialView === 'manual' && canManualTab) {
      setUploadView('manual')
    } else if (initialView === 'auto' && canAutoTab) {
      setUploadView('auto')
    } else if (canManualTab) {
      setUploadView('manual')
    } else if (canAutoTab) {
      setUploadView('auto')
    } else {
      setUploadView('manual')
    }
  }, [initialView, canManualTab, canAutoTab])

  useEffect(() => {
    if (uploadView !== 'auto') {
      setTriggerLabels(null)
      return
    }
    let mounted = true
    systemService.getTriggerLabels().then((labels) => {
      if (mounted) setTriggerLabels(labels)
    })
    return () => {
      mounted = false
    }
  }, [uploadView])
  const resolveNodeLabel = useCallback((node: ProjectNode) => {
    // 자동 업로드 트리에서만 라벨 매핑을 적용
    if (uploadView !== 'auto' || !triggerLabels) return node.name

    // 루트(프로젝트) 노드만 라벨 치환 (예: TRG_FLD → FLD(경작지))
    const isRootLike = !node.parent_id || node.depth <= 0
    if (!isRootLike) return node.name

    const raw = node.name || ''
    if (!raw) return node.name

    // 예: "TRG_FLD" → prefix="TRG", code="FLD"
    const parts = raw.split('_').filter(Boolean)
    if (parts.length < 2) return node.name

    const prefix = parts[0]
    const code = parts[1]

    // prefix 기반으로 해당 rule의 스키마 조회
    const schema = triggerLabels[prefix]
    if (!schema) return node.name

    const category = schema.categories?.[code]
    const label = category?.display_name || category?.label
    if (label) return label

    return node.name
  }, [triggerLabels, uploadView])

  // 🔥 업로드 탭 변경 시 루트 노드 재로딩
  useEffect(() => {
    setSelectedNode(null)
    setSelectedBatch(null)
    setNodeFiles([])
    setFilesSkip(0)
    setFilesHasMore(true)
    setFilesTotal(null)
    setProjectStructure(null)
    setFileCounts({})
    setChildCounts({})
    setExpandedNodes(new Set())
    setPendingUploads([])
    loadRootNodes(uploadView)
  }, [uploadView])

  // 🔥 루트 노드 로드 (children 포함)
  const loadRootNodes = async (method: 'manual' | 'auto') => {
    try {
      setLoading(true)
      setError(null)
      // console.log('📁 루트 노드 로드 시작')
      
      const rootNodes = await rawDataService.getRootNodes(method)
      const sortedRoots = sortNodesByName(rootNodes)
      // console.log('📁 로드된 루트 노드들:', rootNodes)
      
      if (sortedRoots && sortedRoots.length > 0) {
        // 모든 노드를 평면적으로 수집 (루트 노드 + 그들의 children)
        const allNodes: ProjectNode[] = []
        
        sortedRoots.forEach(rootNode => {
          allNodes.push(rootNode)
          
          // 루트 노드에 children이 있으면 추가
          if (rootNode.children && rootNode.children.length > 0) {
            allNodes.push(...sortNodesByName(rootNode.children))
            // console.log(`📁 ${rootNode.name}의 children 추가:`, rootNode.children)
          }
        })
        
        setProjectStructure({
          project: { id: 0 },
          hierarchy: {},
          total_nodes: allNodes.length,
          total_files: 0,
          total_images: 0,
          nodes: allNodes,
          files: []
        })
        
        // console.log('📁 최종 nodes 배열:', allNodes)
      } else {
        setProjectStructure(null)
      }
      
    } catch (error: any) {
      console.error('📁 루트 노드 로드 실패:', error)
      setProjectStructure(null)
    } finally {
      setLoading(false)
    }
  }

  // nodes 변화 시 클라이언트 추정치로 보조 집계하되, 서버에서 받은 값은 덮어쓰지 않음
  useEffect(() => {
    if (!projectStructure?.nodes) return
    const countsFromNodes: Record<number, number> = {}
    for (const n of projectStructure.nodes) {
      if (n.parent_id != null) {
        countsFromNodes[n.parent_id] = (countsFromNodes[n.parent_id] || 0) + 1
      }
    }
    // 기존 서버 기반 childCounts를 우선시하도록 병합 순서: nodes 추정치 먼저, 기존 값이 마지막에 덮어씀
    setChildCounts(prev => ({ ...countsFromNodes, ...prev }))

    // 초기/변경 시점에 현재 보이는 노드 중 리프 후보들에 대해 '히스토리 개수'로 사이드바 숫자 보강
    ;(async () => {
      try {
        const nodeIds = projectStructure.nodes.map(n => n.id)
        const parentIdSet = new Set(projectStructure.nodes.map(n => n.parent_id).filter(Boolean) as number[])
        const leafIds = nodeIds.filter(id => !parentIdSet.has(id))
        const entries = await Promise.all(
          leafIds.map(async (id) => [id, await rawDataService.countUploadHistory(id, uploadView)] as [number, number])
        )
        const histCounts: Record<number, number> = {}
        entries.forEach(([id, cnt]) => { histCounts[id] = cnt })
        setFileCounts(prev => ({ ...prev, ...histCounts }))
      } catch {}
    })()
  }, [projectStructure?.nodes, uploadView])

  // 🗑️ 복잡한 함수들 제거됨 - 간단한 폴더 트리만 유지

  // 📁 노드 확장/축소 토글 (클라이언트 nodes 배열만 활용)
  const toggleNode = async (nodeId: number) => {
    // 현재 기준으로 확장/축소 의도를 먼저 결정해 두고, 실제 상태 갱신은 함수형 업데이트로 수행
    const shouldExpand = !expandedNodes.has(nodeId)

    if (shouldExpand) {
      // 확장 시 서버에서 자식 노드를 가져와 병합하고, 카운트를 즉시 보강
      try {
        const enriched = await rawDataService.getNodeChildren(nodeId, uploadView)
        // 프로젝트 구조에 자식 노드 병합
        setProjectStructure(prev => {
          if (!prev) return prev
          const existingIds = new Set(prev.nodes.map(n => n.id))
          const merged = [...prev.nodes]
          enriched.forEach((c: any) => { if (!existingIds.has(c.id)) merged.push(c) })
          return { ...prev, nodes: sortNodesByName(merged) }
        })

        // 부모 자신의 자식 수 및 자식들의 하위/파일 수 반영
        const countsUpdate: Record<number, number> = { [nodeId]: enriched.length }
        const leafFileCounts: Record<number, number> = {}
        enriched.forEach((c: any) => {
          if (typeof c.child_count === 'number') countsUpdate[c.id] = c.child_count
          if ((c.child_count || 0) === 0 && typeof c.file_count === 'number') leafFileCounts[c.id] = c.file_count
        })
        if (Object.keys(countsUpdate).length > 0) setChildCounts(prev => ({ ...prev, ...countsUpdate }))
        if (Object.keys(leafFileCounts).length > 0) setFileCounts(prev => ({ ...prev, ...leafFileCounts }))
      } catch (e) {
        console.warn('자식 하위 개수 보강 실패:', e)
      }
    }

    setExpandedNodes(prev => {
      const ns = new Set(prev)
      if (shouldExpand) {
        ns.add(nodeId)
      } else {
        ns.delete(nodeId)
      }
      return ns
    })
  }

  // 📁 노드 선택 (왼쪽-오른쪽 싱크)
  const handleNodeSelect = async (node: ProjectNode) => {
    setSelectedNode(node)
    setDescExpanded(false)
    // console.log(`📁 노드 선택됨: ${node.name} (ID: ${node.id})`)
    // 선택 즉시 상세 정보(설명 포함)를 보강
    try {
      const enriched = await rawDataService.getNodeById(node.id, uploadView)
      if (enriched) {
        setSelectedNode(enriched)
      }
    } catch {}
    
    // 부모 노드들을 자동으로 확장 (breadcrumb 경로)
    const parentIds: number[] = []
    let currentNode = projectStructure?.nodes.find(n => n.id === node.id)
    
    while (currentNode?.parent_id) {
      parentIds.unshift(currentNode.parent_id)
      currentNode = projectStructure?.nodes.find(n => n.id === currentNode!.parent_id)
    }
    
    // 부모 노드들을 차례대로 확장
    setExpandedNodes(prev => {
      const ns = new Set(prev)
      for (const parentId of parentIds) {
        if (!ns.has(parentId)) {
          ns.add(parentId)
          console.log(`📁 부모 노드 자동 확장: ${parentId}`)
        }
      }
      return ns
    })

    // 파일 목록 로드 (리프 여부를 서버로 확인 후 진행)
    const clientChildren = projectStructure?.nodes.filter((n: ProjectNode) => n.parent_id === node.id) || []
    if (clientChildren.length > 0) {
      // 확실히 폴더: 파일 조회 안 함
      setNodeFiles([])
      setFilesSkip(0)
      setFilesHasMore(true)
      setFilesTotal(null)
      setPendingUploads([])
    } else {
      // 서버에서 자식 유무 확인
      try {
        const serverChildren = await rawDataService.getNodeChildren(node.id, uploadView)
        if (serverChildren && serverChildren.length > 0) {
          // 폴더로 확정: 노드 병합 반영 및 카운트 갱신, 파일 조회 안 함
          setProjectStructure(prev => {
            if (!prev) return prev
            const existingIds = new Set(prev.nodes.map(n => n.id))
            const merged = [...prev.nodes]
            serverChildren.forEach((c: any) => { if (!existingIds.has(c.id)) merged.push(c) })
            return { ...prev, nodes: sortNodesByName(merged) }
          })
          setChildCounts(prev => ({ ...prev, [node.id]: serverChildren.length }))
          const countsUpdate: Record<number, number> = {}
          serverChildren.forEach((c: any) => {
            if (typeof c.child_count === 'number') countsUpdate[c.id] = c.child_count
          })
          if (Object.keys(countsUpdate).length > 0) setChildCounts(prev => ({ ...prev, ...countsUpdate }))
          // 리프 자식들 히스토리 개수로 업데이트
          const leafChildren = serverChildren.filter((c: any) => (c.child_count || 0) === 0)
          if (leafChildren.length > 0) {
            try {
              const entries = await Promise.all(
                leafChildren.map(async (lc: any) => [lc.id, await rawDataService.countUploadHistory(lc.id, uploadView)] as [number, number])
              )
              const histCounts: Record<number, number> = {}
              entries.forEach(([id, cnt]) => { histCounts[id] = cnt })
              setFileCounts(prev => ({ ...prev, ...histCounts }))
            } catch {}
          }

          setNodeFiles([])
          setFilesSkip(0)
          setFilesHasMore(true)
          setFilesTotal(null)
          setPendingUploads([])
          setSelectedBatch(null)

          // 서버에서 자식이 확인된 경우, 좌측 트리를 즉시 확장하여 3단계가 보이도록 함
          setExpandedNodes(prev => {
            const ns = new Set(prev)
            ns.add(node.id)
            return ns
          })
        } else {
          // 리프로 확정: 초기 진입 시 히스토리 선택 없이 전체 업로드 목록을 불러온다
          const ctrl = beginFileListRequest()
          try {
            const [items, totalFiles] = await Promise.all([
              rawDataService.getNodeFiles(node.id, 0, 50, uploadView, ctrl.signal),
              rawDataService.getNodeFilesCount(node.id, uploadView, ctrl.signal)
            ])
            if (ctrl.signal.aborted) return
            setNodeFiles(items)
            setFilesSkip(items.length)
            setFilesHasMore(items.length === 50)
            setFilesTotal(totalFiles)
            const totalHist = await rawDataService.countUploadHistory(node.id, uploadView)
            setFileCounts(prev => ({ ...prev, [node.id]: totalHist }))
          } catch (e) {
            if (isAbortError(e)) return
            setNodeFiles([])
            setFilesSkip(0)
            setFilesHasMore(false)
            setFilesTotal(null)
          }
          setPendingUploads([])
          setSelectedBatch(null)
        }
      } catch (e) {
        // 서버 확인 실패 시 보수적으로 폴더로 간주 (불필요한 파일 조회 방지)
        console.warn('서버 자식 확인 실패, 폴더로 간주:', e)
        setNodeFiles([])
        setFilesSkip(0)
        setFilesHasMore(true)
        setFilesTotal(null)
        setPendingUploads([])
      }
    }
  }

  // 재개 파일 선택 핸들러 (숨김 input)
  const handleResumeFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedNode || !resumeTarget) return
    const file = files[0]
    // 파일명 검증 (간단 확인)
    if (file.name !== resumeTarget.filename) {
      alert('선택한 파일명이 기존 업로드와 다릅니다. 동일한 파일을 선택해주세요.')
      e.target.value = ''
      return
    }
    try {
      setUploading(true)
      setUploadProgress(0)
      const projectId = selectedNode.project_id || 0
      // 재개는 기존 배치 설정을 그대로 사용해야 하므로 전처리 옵션 전달 없이 업로드
      await rawDataService.uploadFile(file, selectedNode.id, projectId, (p) => setUploadProgress(p))
      await new Promise((r) => setTimeout(r, 150))
      alert('업로드가 완료되었습니다.')
      // 방금 업로드한 배치의 파일들을 기본으로 보여주기 위해 히스토리를 새로고침 후 첫 배치를 로드
      try {
        const history = await rawDataService.listUploadHistory(selectedNode.id, 0, 1, uploadView)
        if (history && history.length > 0) {
          const items = await rawDataService.listUploadHistoryItems(history[0].id, 0, 50)
          setSelectedBatch(history[0])
          setNodeFiles(items)
          setFilesSkip(items.length)
          setFilesHasMore(items.length === 50)
          setFilesTotal(null)
        } else {
          const refreshed = await rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView)
          setNodeFiles(refreshed)
          setFilesSkip(50)
          setFilesHasMore(refreshed.length === 50)
                                        setFilesTotal(null)  // 재개 업로드 후에는 null로 설정
        }
      } catch {
        const refreshed = await rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView)
        setNodeFiles(refreshed)
        setFilesSkip(50)
        setFilesHasMore(refreshed.length === 50)
      }
      // 미완료 목록 갱신
      const pending = await rawDataService.listPendingSessions({ projectId: projectId, nodeId: selectedNode.id })
      setPendingUploads(pending.map(p => ({ key: p.key, session_id: p.session_id, filename: p.filename, received: p.received, total_size: p.total_size })))
    } catch (err: any) {
      if (err && typeof err.message === 'string' && err.message === 'UPLOAD_ABORTED') {
        // 사용자가 취소한 경우: 조용히 종료
        console.info('업로드가 사용자에 의해 취소되었습니다.')
      } else {
        console.error('업로드 재개 실패:', err)
        alert('업로드 재개에 실패했습니다.')
      }
    } finally {
      setUploading(false)
      setUploadProgress(0)
      setResumeTarget(null)
      e.target.value = ''
    }
  }

  const handleClickResume = (item: { key: string; session_id: string; filename: string }) => {
    setResumeTarget({ key: item.key, session_id: item.session_id, filename: item.filename })
    // 숨김 파일 선택기 열기
    resumeFileInputRef.current?.click()
  }

  const handleClickAbort = async (item: { key: string; session_id: string }) => {
    if (!selectedNode) return
    if (!window.confirm('미완료 업로드를 중단하시겠습니까?')) return
    try {
      await rawDataService.abortUploadSession(item.session_id, item.key)
      const pending = await rawDataService.listPendingSessions({ projectId: selectedNode.project_id!, nodeId: selectedNode.id })
      setPendingUploads(pending.map(p => ({ key: p.key, session_id: p.session_id, filename: p.filename, received: p.received, total_size: p.total_size })))
      alert('업로드 세션을 중단했습니다.')
    } catch (err) {
      console.error('업로드 중단 실패:', err)
      alert('업로드 중단에 실패했습니다.')
    }
  }

  const handleManualUploadConfirm = async (files: FileList | null, modes: { face: boolean; plate: boolean }) => {
    if (!files || !selectedNode) {
      setIsManualUploadModalOpen(false)
      return
    }
    setManualPreprocessModes(modes)
    await handleFileUploadInternal(files, modes)
    setIsManualUploadModalOpen(false)
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = event.target
    const files = inputEl.files
    if (!files) return
    const resetInput = () => {
      if (inputEl) {
        inputEl.value = ''
      }
    }
    await handleFileUploadInternal(files, undefined, resetInput)
    resetInput()
  }

  const handleFileUploadInternal = async (
    files: FileList,
    modes?: { face: boolean; plate: boolean },
    onReset?: () => void
  ) => {
    if (!selectedNode) return

    try {
      setUploading(true)
      setUploadProgress(0)
      const projectId = selectedNode.project_id || 0
      const all = Array.from(files)
      
      // 중복 파일 체크
      const duplicateFiles: string[] = []
      for (const file of all) {
        try {
          const duplicateCheck = await rawDataService.checkDuplicateUpload(selectedNode.id, file.name)
          if (duplicateCheck.is_duplicate) {
            duplicateFiles.push(file.name)
          }
        } catch (error) {
          console.warn('중복 체크 실패:', file.name, error)
          // 중복 체크 실패 시에는 업로드 진행 (서버에서 추가 검증)
        }
      }
      
      // 중복 파일이 있으면 사용자에게 알림 후 업로드 중단
      if (duplicateFiles.length > 0) {
        const fileList = duplicateFiles.join(', ')
        alert(`⚠️ 중복된 파일이 있어 업로드를 중단합니다:\n\n${fileList}\n\n이미 업로드된 파일과 동일한 이름과 확장자의 파일입니다.`)
        setUploading(false)
        onReset?.()
        return
      }
      
      localStorage.setItem('upload_batch_total', String(all.length))
      for (let i = 0; i < all.length; i++) {
        const file = all[i]
        localStorage.setItem('upload_batch_index', String(i + 1))
        setUploadProgress(0)
        await rawDataService.uploadFile(
          file,
          selectedNode.id,
          projectId,
          (p) => setUploadProgress(p),
          modes ?? manualPreprocessModes
        )
      }
      localStorage.removeItem('upload_batch_total')
      localStorage.removeItem('upload_batch_index')
      // 알림을 약간 늦춰 진행률 100% 렌더 보장
      await new Promise((r) => setTimeout(r, 150))
      alert('업로드가 완료되었습니다.')

      // 업로드 후 목록/트리 새로고침: 히스토리 카운트와 "전체 보기" 파일 목록을 즉시 반영
      try {
        const [totalHist, allFiles, totalFiles] = await Promise.all([
          rawDataService.countUploadHistory(selectedNode.id, uploadView),
          rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView),
          rawDataService.getNodeFilesCount(selectedNode.id, uploadView)
        ])
        setFileCounts(prev => ({ ...prev, [selectedNode.id]: totalHist }))
        setSelectedBatch(null)
        setNodeFiles(allFiles)
        setFilesSkip(allFiles.length)
        setFilesHasMore(allFiles.length === 50)
        setFilesTotal(totalFiles)
      } catch {
        setSelectedBatch(null)
        setNodeFiles([])
        setFilesSkip(0)
        setFilesHasMore(false)
        setFilesTotal(null)
      }
    } catch (error: any) {
      if (error && typeof error.message === 'string' && error.message === 'UPLOAD_ABORTED') {
        // 사용자가 취소한 경우: 메시지/에러 표시 없이 종료
        console.info('업로드가 사용자에 의해 취소되었습니다.')
      } else {
        setError('파일 업로드에 실패했습니다.')
        console.error('📁 업로드 실패:', error)
      }
    } finally {
      setUploading(false)
    }
  }

  // 삭제 기능 제거됨


  // 📁 원천데이터 생성 처리 (에러 메시지 개선)
  const handleCreateRawData = async (data: RawDataFormData) => {
    try {
      // console.log('📁 원천데이터 생성 시작:', data)
      await rawDataService.createRawData(data)
      
      // console.log('📁 루트 노드 새로고침 시작')
      // 루트 노드 새로고침 및 좌측 패널 재계산
      await loadRootNodes(uploadView)
      // 생성 직후에는 선택 노드를 초기화하여 새 구조가 보이도록 함
      setSelectedNode(null)
      setExpandedNodes(new Set())
      setNodeFiles([])
      setFilesSkip(0)
      setFilesHasMore(true)
      setFilesTotal(null)
      // console.log('📁 루트 노드 새로고침 완료')
    } catch (error: any) {
      console.error('📁 원천데이터 생성 실패:', error)
      
      // 에러 메시지를 그대로 전달 (모달에서 처리)
      throw error
    }
  }

  // 트리 노드 렌더링
  // 루트 프로젝트를 출처(리전)로 묶는다. 국내 원생(source_region 없음)이 먼저, 그다음 가져온 리전.
  // 국내 것만 있을 때는 머리글을 안 붙여 지금 화면과 같다. 리전이 늘면 묶음이 하나 늘 뿐이다.
  const REGION_LABELS: Record<string, string> = { kr: '국내', us: '북미', eu: '유럽' }
  const regionLabel = (code?: string | null) => (code ? (REGION_LABELS[code] || code.toUpperCase()) : '국내')
  const renderRootGroups = (roots: ProjectNode[]) => {
    const groups = new Map<string, ProjectNode[]>()
    roots.forEach((n) => {
      const key = n.source_region || ''
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(n)
    })
    const keys = Array.from(groups.keys()).sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    if (keys.length <= 1) {
      return roots.map((node) => renderTreeNode(node))
    }
    return keys.map((key) => (
      <div key={`region-${key || 'local'}`} className="mb-2">
        <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
          {regionLabel(key || null)}
        </div>
        {groups.get(key)!.map((node) => renderTreeNode(node))}
      </div>
    ))
  }

  const renderTreeNode = (node: ProjectNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id)
    
    // 자식 노드 여부: 서버 제공 child_count 우선, 없으면 클라이언트 계산
    const childCountFromClient = childCounts[node.id] ?? 0
    const hasChildren = childCountFromClient > 0

    // 리프 여부는 선택/확장 시 서버 확인 뒤에만 파일 수를 갱신합니다.

    return (
      <div key={node.id}>
        <div
          className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${
            selectedNode?.id === node.id 
              ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' 
              : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={async () => {
            handleNodeSelect(node)
            if (hasChildren && !expandedNodes.has(node.id)) {
              await toggleNode(node.id)
            }
          }}
        >
          {/* 폴더 아이콘 */}
          <div className="mr-2">
            <svg 
              className="w-5 h-5 text-gray-700" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
          </div>

          {/* 확장/축소 화살표 */}
          {hasChildren && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                await toggleNode(node.id)
              }}
              className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
            >
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* 노드 이름 + 히스토리 개수 (리프에서만) */}
          <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
            {resolveNodeLabel(node)}
            {hasChildren
              ? ` (${childCountFromClient})`
              : ` (${fileCounts[node.id] != null ? fileCounts[node.id] : 0})`}
          </span>
        </div>

        {/* 하위 노드들 */}
        {hasChildren && isExpanded && projectStructure?.nodes && (
          <div>
            {projectStructure.nodes
              .filter((n: ProjectNode) => n.parent_id === node.id)
              .filter((child: ProjectNode, index: number, self: ProjectNode[]) => 
                // 중복 렌더링 방지: 같은 ID를 가진 노드는 한 번만 렌더링
                index === self.findIndex((n: ProjectNode) => n.id === child.id)
              )
              .sort((a: ProjectNode, b: ProjectNode) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
              .map((child: ProjectNode) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 업로드 이벤트에 맞춰 현재 노드의 "전체 보기" 파일 목록을 즉시 재로딩 (미완료 배치가 있어도 기존 완료 파일 유지 표시)
  const reloadCurrentNodeFiles = useCallback(async () => {
    if (!selectedNode) return
    // 리프 폴더에서만 파일 목록 표시
    const childNodes = projectStructure?.nodes.filter((n: ProjectNode) => n.parent_id === selectedNode.id) || []
    const isLeafFolder = childNodes.length === 0
    if (!isLeafFolder) return
    try {
      const [items, totalFiles] = await Promise.all([
        rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView),
        rawDataService.getNodeFilesCount(selectedNode.id, uploadView)
      ])
      setSelectedBatch(null)
      setNodeFiles(items)
      setFilesSkip(items.length)
      setFilesHasMore(items.length === 50)
      setFilesTotal(totalFiles)
    } catch {
      setSelectedBatch(null)
      setNodeFiles([])
      setFilesSkip(0)
      setFilesHasMore(false)
      setFilesTotal(null)
    }
  }, [selectedNode, projectStructure?.nodes, uploadView])

  useEffect(() => {
    const onStarted = () => { reloadCurrentNodeFiles() }
    const onCompleted = () => { reloadCurrentNodeFiles() }
    window.addEventListener('upload:started', onStarted)
    window.addEventListener('upload:completed', onCompleted)
    return () => {
      window.removeEventListener('upload:started', onStarted)
      window.removeEventListener('upload:completed', onCompleted)
    }
  }, [reloadCurrentNodeFiles])

  useEffect(() => {
    if (!selectedNode) return
    setSelectedBatch(null)
    reloadCurrentNodeFiles()
  }, [uploadView, selectedNode, reloadCurrentNodeFiles])


  const [isManualUploadModalOpen, setIsManualUploadModalOpen] = useState(false)

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button 
            onClick={() => setError(null)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            닫기
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* 헤더 */}
      <div className="mb-4 flex flex-col gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">원천 데이터 관리</h1>
          <p className="text-gray-600">원천 데이터 데이터 구조를 탐색하고 관리합니다.</p>
        </div>
        {!hideViewToggle && (canManualTab || canAutoTab) && (
          <div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
              {canManualTab && (
                <button
                  className={`px-4 py-2 text-sm rounded-md transition-colors ${
                    uploadView === 'manual' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'
                  }`}
                  onClick={() => { setUploadView('manual'); clearSearch() }}
                  type="button"
                  aria-pressed={uploadView === 'manual'}
                >
                  수동 업로드
                </button>
              )}
              {canAutoTab && (
                <button
                  className={`px-4 py-2 text-sm rounded-md transition-colors ${
                    uploadView === 'auto' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-900'
                  }`}
                  onClick={() => { setUploadView('auto'); clearSearch() }}
                  type="button"
                  aria-pressed={uploadView === 'auto'}
                >
                  자동 업로드
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 로딩 상태 표시 */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      )}

      {/* 메인 컨텐츠 영역 */}
      {!loading && (
        <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
          {/* 좌측: 원천 데이터 트리 */}
          <div className="w-80 border-r border-gray-200 bg-gray-50">
            <div className="p-4 min-h-16 flex flex-col justify-center border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">원천 데이터 구조</h3>
                {uploadView === 'manual' && canManualTab && hasAnyCreateProjectPermission && (
                  <button
                    onClick={openCreateModal}
                    className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900 transition-colors"
                  >
                    + 원천데이터 생성
                  </button>
                )}
              </div>
            </div>
            <div className="p-2 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
              {!projectStructure ? (
                <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 350px)' }}>
                  <div className="text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm mb-2">등록된 원천 데이터가 없습니다</p>
                    <p className="text-xs text-gray-400 mb-3">새로운 원천 데이터를 생성해주세요</p>
                    {uploadView === 'manual' && canManualTab && (
                    <button 
                      onClick={openCreateModal}
                      className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                    >
                      원천데이터 생성
                    </button>
                    )}
                  </div>
                </div>
              ) : (
                renderRootGroups(
                  projectStructure.nodes
                    .filter((node: ProjectNode) => !node.parent_id)
                    .sort((a: ProjectNode, b: ProjectNode) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                )
              )}
            </div>
          </div>

          {/* 우측: 선택된 노드 내용 */}
          <div className="flex-1 bg-white">
            <div className="p-4 min-h-16 flex flex-col justify-center border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedNode ? resolveNodeLabel(selectedNode) : '노드를 선택하세요'}
                  </h3>
                  {selectedNode && (
                      <p className="text-sm text-gray-600 mt-1 bg-green-150">
                        생성일: {formatShortDate(selectedNode.created_at)}
                      </p>
                  )}
                </div>
              
                {selectedNode && (() => {
                  // 가장 하위폴더인지 확인 (하위 노드가 없는 경우)
                  const childNodes = projectStructure?.nodes.filter((n: ProjectNode) => n.parent_id === selectedNode.id) || []
                  const isLeafFolder = childNodes.length === 0

                  // depth 계산 (루트=1, 부모를 타고 올라가며 +1)
                  const depth = (() => {
                    let d = 1
                    let current: ProjectNode | undefined = selectedNode as ProjectNode
                    const allNodes = projectStructure?.nodes || []
                    while (current?.parent_id) {
                      d += 1
                      current = allNodes.find((n: ProjectNode) => n.id === (current as ProjectNode).parent_id)
                    }
                    return d
                  })()

                  const actions = (selectedNode as any).actions as Record<string, boolean> | undefined
                  const nodeAllowsUpload = actions ? !!actions.manual_upload : true
                  const canUpload = isLeafFolder && depth >= 3 && nodeAllowsUpload && canManualTab
                  return (uploadView === 'manual' && canUpload) ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setIsManualUploadModalOpen(true)}
                        className="px-3 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-900 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path d="M12 3v12"/>
                          <path d="m17 8-5-5-5 5"/>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        </svg>
                        파일 업로드
                      </button>
                    </div>
                  ) : null
                })()}
              </div>
            </div>
            {/* 검색바: 노드 선택 시 표시 */}
            {selectedNode && (
              <div className="px-4 py-2 border-b border-gray-200 bg-white">
                <form
                  className="flex items-center gap-2"
                  onSubmit={e => { e.preventDefault(); handleSearch() }}
                >
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="파일명 또는 메타데이터 값 검색..."
                    spellCheck={false}
                    autoComplete="off"
                    className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                  <input
                    type="date"
                    value={searchDateFrom}
                    onChange={e => setSearchDateFrom(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                  <span className="text-gray-400 text-sm flex-shrink-0">~</span>
                  <input
                    type="date"
                    value={searchDateTo}
                    onChange={e => setSearchDateTo(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />

                  <label className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={searchScopeNode}
                      onChange={e => setSearchScopeNode(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-400"
                    />
                    현재 노드
                  </label>
                  <button
                    type="submit"
                    disabled={searchLoading}
                    className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50 flex-shrink-0"
                  >
                    {searchLoading ? '검색 중...' : '검색'}
                  </button>
                  {searchResults !== null && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex-shrink-0"
                    >
                      초기화
                    </button>
                  )}
                </form>
              </div>
            )}

            {/* 설명 파트: 헤더 바로 아래 */}
            {selectedNode && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-start mb-1 gap-2">
                  <div className="text-base font-semibold text-gray-900">설명</div>
                  <button
                    type="button"
                    className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                    onClick={() => setDescExpanded(v => !v)}
                    aria-expanded={descExpanded}
                    aria-label={descExpanded ? '설명 접기' : '설명 더보기'}
                  >
                    {descExpanded ? '접기' : '더보기'}
                  </button>
                </div>
                <div className={`text-sm whitespace-pre-wrap break-all overflow-hidden leading-relaxed transition-all ${descExpanded ? '' : 'max-h-24 overflow-hidden'}`}>
                  {selectedNode.description && String(selectedNode.description).trim().length > 0
                    ? <span className="text-gray-700">{selectedNode.description}</span>
                    : <span className="text-gray-400 italic">입력한 내용이 없습니다</span>}
                </div>
              </div>
            )}

            <div className="p-4 overflow-y-scroll" style={{ height: 'calc(100vh - 260px)' }}>

              {/* 검색 결과 뷰 */}
              {searchResults !== null ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600">
                      {searchTotal != null && (
                        <span className="font-semibold text-gray-900 mr-1">총 {searchTotal.toLocaleString()}건</span>
                      )}
                      {searchResults.length > 0 && searchTotal != null && searchResults.length < searchTotal && (
                        <span>({searchResults.length.toLocaleString()}건 표시 중)</span>
                      )}
                    </span>
                    <button
                      onClick={handleSearchDownload}
                      disabled={downloadLoading || searchResults.length === 0}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {downloadLoading ? (downloadStatusMsg || '준비 중...') : 'ZIP 다운로드'}
                    </button>
                  </div>

                  {searchLoading && searchResults.length === 0 ? (
                    <div className="flex justify-center py-12 text-gray-500">검색 중...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-2">
                      <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p>검색 결과가 없습니다.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map(file => {
                        const fullSrc = `${config.API_BASE_URL}/files/${file.id}/image`
                        const thumbSrc = file.thumbnail_url
                          ? (file.thumbnail_url.startsWith('http') ? file.thumbnail_url : `${config.API_BASE_URL}${file.thumbnail_url}`)
                          : null
                        const displayName = file.original_filename ?? file.filename

                        // 메타데이터에서 주요 필드 추출
                        const meta = file.metadata || {}
                        const vinId = meta.upload_trigger_data?.VINID
                        const machine = meta.common_data?.machine
                        const workDevice = meta.can_data?.work_device_model

                        return (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
                            onClick={() => setPreviewSrc(fullSrc)}
                          >
                            <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                              {thumbSrc ? (
                                <img src={thumbSrc} alt={displayName} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">없음</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate text-sm" title={displayName}>{displayName}</div>
                              <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                {vinId && <span>VINID: {vinId}</span>}
                                {machine && <span>{machine}</span>}
                                {workDevice && <span>{workDevice}</span>}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5 truncate" title={file.node_path}>
                                {file.node_path} · {formatShortDate(file.created_at)}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {searchHasMore && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={handleSearchLoadMore}
                        disabled={searchLoading}
                        className="px-4 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        {searchLoading ? '로딩 중...' : '더보기'}
                      </button>
                    </div>
                  )}
                </div>
              ) : selectedNode ? (
                (() => {
                  const childNodes = projectStructure?.nodes.filter((n: ProjectNode) => n.parent_id === selectedNode.id) || []
                  const hasChildNodes = childNodes.length > 0

                  return (
                    <div className="space-y-4">
                      {hasChildNodes ? (
                        /* 📁 부모폴더: 하위 폴더들만 표시 */
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">하위 폴더 ({childNodes.length}개)</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {childNodes.map((childNode: ProjectNode) => (
                              <div
                                key={childNode.id}
                                className="p-3 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
                                onClick={() => handleNodeSelect(childNode)}
                              >
                                <div className="flex items-center">
                                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                                  </svg>
                                  <div>
                                    <div className="font-medium text-gray-900">{childNode.name}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* 📄 가장 하위폴더: 히스토리 + 파일 표시 */
                        <div>
                          {/* 히스토리 섹션 */}
                          <UploadHistorySection
                            key={`${selectedNode.id}-${uploadView}`}
                            nodeId={selectedNode.id}
                            uploadMethod={uploadView}
                            readOnly={uploadView === 'auto'}
                            canDelete={Boolean((selectedNode as any).actions?.delete)}
                            canDownload={Boolean((selectedNode as any).actions?.download)}
                            selectedBatchId={selectedBatch?.id ?? null}
                            onSelectBatch={async (batch) => {
                            setSelectedBatch(batch)
                            const ctrl = beginFileListRequest()
                            if (batch) {
                              try {
                                const items = await rawDataService.listUploadHistoryItems(batch.id, 0, 50, ctrl.signal)
                                if (ctrl.signal.aborted) return
                                setNodeFiles(items)
                                setFilesSkip(items.length)
                                setFilesHasMore(items.length === 50)
                                setFilesTotal(batch.total_items) // 배치의 전체 아이템 개수 설정
                              } catch (err) {
                                if (isAbortError(err)) return
                                console.error('히스토리 파일 조회 실패:', err)
                                alert('히스토리 상세를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
                                setNodeFiles([])
                                setFilesSkip(0)
                                setFilesHasMore(false)
                                setFilesTotal(null)
                              }
                            } else {
                              // 히스토리 선택 해제 시: 해당 노드 전체 업로드 목록 표시
                              try {
                                const [items, totalFiles] = await Promise.all([
                                  rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView, ctrl.signal),
                                  rawDataService.getNodeFilesCount(selectedNode.id, uploadView, ctrl.signal)
                                ])
                                if (ctrl.signal.aborted) return
                                setNodeFiles(items)
                                setFilesSkip(items.length)
                                setFilesHasMore(items.length === 50)
                                setFilesTotal(totalFiles)
                              } catch (e) {
                                if (isAbortError(e)) return
                                setNodeFiles([])
                                setFilesSkip(0)
                                setFilesHasMore(false)
                                setFilesTotal(null)
                              }
                            }
                          }} onAfterDelete={async () => {
                            try {
                              // 좌측 히스토리 개수 갱신
                              const totalHist = await rawDataService.countUploadHistory(selectedNode.id, uploadView)
                              setFileCounts(prev => ({ ...prev, [selectedNode.id]: totalHist }))
                            } catch {}
                            // 파일 목록도 최신으로 갱신
                            try {
                              const [items, totalFiles] = await Promise.all([
                                rawDataService.getNodeFiles(selectedNode.id, 0, 50, uploadView),
                                rawDataService.getNodeFilesCount(selectedNode.id, uploadView)
                              ])
                              setNodeFiles(items)
                              setFilesSkip(items.length)
                              setFilesHasMore(items.length === 50)
                              setFilesTotal(totalFiles)
                            } catch {
                              setNodeFiles([])
                              setFilesSkip(0)
                              setFilesHasMore(false)
                              setFilesTotal(null)
                            }
                          }} onResume={(expectedName: string) => {
                            // 전역 재개 로직: 숨김 인풋 열고 같은 파일을 선택하도록 유도
                            const input = document.createElement('input')
                            input.type = 'file'
                            input.onchange = async (e: any) => {
                              const file = e?.target?.files?.[0]
                              if (!file) return
                              // expectedName이 확장자를 포함하지 않는 경우(히스토리의 stem)도 허용
                              const exp = (expectedName || '').trim()
                              const fileName = (file.name || '').trim()
                              const fileStem = fileName.replace(/\.[^.]+$/, '')
                              const matches = exp.includes('.')
                                ? exp.toLowerCase() === fileName.toLowerCase()
                                : exp.toLowerCase() === fileStem.toLowerCase()
                              if (!matches) {
                                alert('동일한 파일을 선택해주세요. (파일명 또는 파일명+확장자)')
                                return
                              }
                              try {
                                setUploading(true)
                                setUploadProgress(0)
                                const projectId = selectedNode?.project_id || 0
                                await rawDataService.uploadFile(
                                  file as File,
                                  selectedNode!.id,
                                  projectId,
                                  (p) => setUploadProgress(p),
                                  manualPreprocessModes
                                )
                                alert('업로드가 완료되었습니다.')
                              } catch (err) {
                                console.error('이어받기 실패:', err)
                                alert('이어받기에 실패했습니다.')
                              } finally {
                                setUploading(false)
                                setUploadProgress(0)
                              }
                            }
                            input.click()
                          }} />
                          <h4 className="font-medium text-gray-900 mb-3">파일 ({filesTotal != null && filesTotal > 0 ? `${nodeFiles.length}/${filesTotal}` : `${nodeFiles.length}개`})</h4>
                          {/* 정책: 활성 업로드는 전역 배너만 사용. 활성 중에는 미완료 안내 완전 비활성화 */}
                          {uploadView === 'manual' && pendingUploads.length > 0 && !rawDataService.getActiveSessionId() && (
                            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                              <div className="flex items-center justify-between">
                                <span>미완료 업로드가 있습니다. 동일 파일로 재개할 수 있습니다.</span>
                              </div>
                              <ul className="mt-2 space-y-2">
                                {pendingUploads.map(u => (
                                  <li key={u.key} className="flex items-center justify-between">
                                    <span>
                                      {u.filename} {u.received != null && u.total_size != null ? `(${Math.round((u.received / u.total_size) * 100)}%)` : ''}
                                    </span>
                                    <span className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleClickResume(u)}
                                        className="px-2 py-1 text-xs bg-gray-800 text-white rounded disabled:opacity-50"
                                        disabled={uploading}
                                      >재개</button>
                                      <button
                                        onClick={() => handleClickAbort(u)}
                                        className="px-2 py-1 text-xs bg-white border border-yellow-400 text-yellow-700 rounded disabled:opacity-50"
                                        disabled={uploading}
                                      >중단</button>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                              {/* 숨김 파일 입력: 재개 시 동일 파일 선택 */}
                              <input
                                ref={resumeFileInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleResumeFileChosen}
                                disabled={uploading}
                              />
                            </div>
                          )}
                          {nodeFiles.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {nodeFiles.map(file => (
                                <div
                                  key={file.id}
                                  className="p-4 border border-gray-200 rounded-lg hover:border-gray-400 hover:shadow-md transition-all"
                                >
                                      <div className="mb-2 relative h-40">
                                        {(() => {
                                          const part = (file.thumbnail_url && file.thumbnail_url.length > 0) ? file.thumbnail_url : `/files/${file.id}/thumbnail`
                                          const src = part.startsWith('http') ? part : `${config.API_BASE_URL}${part}`
                                          // 클릭 프리뷰는 썸네일이 아닌 원본(전처리 이미지)을 사용
                                          const fullSrc = `${config.API_BASE_URL}/files/${file.id}/image`
                                          return (
                                            <>
                                              <img
                                                src={src}
                                                alt={file.filename}
                                                loading="lazy"
                                                className="w-full h-full object-cover rounded cursor-zoom-in"
                                                onClick={() => setPreviewSrc(fullSrc)}
                                                onError={(e) => {
                                                  const img = e.currentTarget as HTMLImageElement
                                                  img.style.display = 'none'
                                                  const ph = img.parentElement?.querySelector('.thumb-fallback') as HTMLElement | null
                                                  if (ph) { ph.style.display = 'flex' }
                                                }}
                                              />
                                              <div className="thumb-fallback absolute inset-0 items-center justify-center bg-gray-200 rounded text-gray-500 text-sm" style={{ display: 'none' }}>이미지 없음</div>
                                            </>
                                          )
                                        })()}
                                      </div>
                                      {/* 파일명 */}
                                      <div className="font-medium text-gray-900 truncate max-w-full block" title={file.original_filename ?? file.filename}>
                                        {file.original_filename ?? file.filename}
                                      </div>
                                  
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <div>크기: {formatFileSize(file.file_size)}</div>
                                    <div>업로드: {formatShortDate(file.created_at)}</div>
                                    {(() => { const name=(file.original_filename||file.filename)||''; const ext=(name.lastIndexOf('.')>=0? name.slice(name.lastIndexOf('.')+1): (file.file_type||'')); return <div>타입: {ext.toLowerCase()}</div> })()}
                                  </div>

                                  
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-col justify-center items-center h-[18rem] text-center py-8 text-gray-500 bg-gray-50 rounded-lg gap-2">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
                                <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
                                <path d="M10 9H8"/>
                                <path d="M16 13H8"/>
                                <path d="M16 17H8"/>
                              </svg>
                              <p>표시할 파일이 없습니다.</p>
                            </div>
                          )}
                          
                          {(filesHasMore && nodeFiles.length > 0) && (
                            <div className="mt-3 flex justify-center">
                              <button
                                className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                                onClick={async () => {
                                  if (!selectedNode) return
                                  const ctrl = beginFileListRequest()
                                  try {
                                    if (selectedBatch) {
                                      const more = await rawDataService.listUploadHistoryItems(selectedBatch.id, filesSkip, 50, ctrl.signal)
                                      if (ctrl.signal.aborted) return
                                      setNodeFiles(prev => [...prev, ...more])
                                      setFilesSkip(prev => prev + more.length)
                                      if (more.length < 50) setFilesHasMore(false)
                                    } else {
                                      const more = await rawDataService.getNodeFiles(selectedNode.id, filesSkip, 50, uploadView, ctrl.signal)
                                      if (ctrl.signal.aborted) return
                                      setNodeFiles(prev => [...prev, ...more])
                                      setFilesSkip(prev => prev + more.length)
                                      if (more.length < 50) setFilesHasMore(false)
                                    }
                                  } catch (e) {
                                    if (isAbortError(e)) return
                                    console.error('더보기 실패:', e)
                                  }
                                }}
                              >더보기</button>
                            </div>
                          )}
                          {/* 빈 폴더 메시지 제거 (요청에 따라 미표시) */}
                        </div>
                      )}
                    </div>
                  )
                })()
              ) : (
                <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
                  <div className="text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <p className="text-lg font-medium">노드를 선택하세요</p>
                    <p className="text-sm text-center">좌측의 원천 데이터 구조에서 노드를 클릭하여 내용을 확인하세요.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 원천데이터 생성 모달 */}
      <CreateRawDataModal 
        isOpen={isCreateModalOpen} 
        onClose={closeCreateModal}
        onSubmit={handleCreateRawData}
        uploadMode={uploadView}
        canDirectProjectInput={canDirectRawProjectInput}
      />
      <ManualUploadModal
        isOpen={isManualUploadModalOpen}
        onClose={() => setIsManualUploadModalOpen(false)}
        onSelectFiles={handleManualUploadConfirm}
      />
      {previewSrc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setPreviewSrc(null)}
          role="dialog"
          aria-label="이미지 미리보기"
        >
          <img
            src={previewSrc}
            alt="preview"
            className="max-w-[90vw] max-h-[90vh] object-contain shadow-lg rounded"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded px-3 py-1"
            onClick={() => setPreviewSrc(null)}
          >닫기</button>
        </div>
      )}
    </div>
  )
}
